import React, { useState } from "react";
import { Plus, Edit2, Trash2, Container, Globe, Edit2Icon, EditIcon } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import {
  useAddEnvironmentVariableMutation,
  useCreateEnvironmentMutation,
  useDeleteEnvironmentVariableMutation,
  useGetEnvironmentsQuery,
  useGetEnvironmentVariablesQuery,
  useDeleteEnvironment,
  useAddGlobalVariable,
  useGetGlobalVariables,
  useDeleteGlobalVariable,
  useUpdateVariableSource,
  useUpdateEnvironmentVariableMutation,
} from "../../../useQuery";

import { useNotification } from "../../../hooks";
import VariableSourceEditor from "./VariableSourceEditor";

const Variables = () => {
  const notify = useNotification();
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [newVariable, setNewVariable] = useState({ key: "", value: "" });
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddEnvironmentForm, setShowAddEnvironmentForm] = useState(false);
  const [newEnvironment, setNewEnvironment] = useState({ name: "" });
  const [showGlobalVariables, setShowGlobalVariables] = useState(false);
  const [showAddGlobalForm, setShowAddGlobalForm] = useState(false);
  const [newGlobalVariable, setNewGlobalVariable] = useState({ key: "", value: "" });
  const [editingVariable, setEditingVariable] = useState(null);
  const [deleteVariableDialog, setDeleteVariableDialog] = useState({
    open: false,
    variable: null,
  });
  const [deleteEnvironmentDialog, setDeleteEnvironmentDialog] = useState({
    open: false,
    environment: null,
  });

  const isDisabled = !(
    (newVariable.key.trim() && newVariable.value.trim()) ||
    (newGlobalVariable.key.trim() && newGlobalVariable.value.trim())
  );

  const queryClient = useQueryClient();

  const { data: environmentsData, isLoading: environmentsLoading } = useGetEnvironmentsQuery();

  const { data: variablesData, isLoading: variablesLoading } = useGetEnvironmentVariablesQuery(
    selectedEnvironment,
    { enabled: !!selectedEnvironment && !showGlobalVariables },
  );

  const { mutate: updateVariable, isLoading: updatingVariable } =
    useUpdateEnvironmentVariableMutation({
      onSuccess: () => {
        queryClient.invalidateQueries(["environmentVariables", selectedEnvironment]);
        notify("Variable updated successfully!", "success");
      },
      onError: (error) => {
        notify(`Error updating variable: ${error.message}`, "error");
      },
    });

  const { data: globalVariablesData, isLoading: globalVariablesLoading } = useGetGlobalVariables({
    enabled: showGlobalVariables,
  });

  const environments = environmentsData || [];
  const currentVariables = variablesData || [];
  const globalVariables = globalVariablesData || [];

  const addVariableMutation = useAddEnvironmentVariableMutation({
    onSuccess: () => {
      notify("Environment Variable Created Successfully!", "success");
      queryClient.invalidateQueries(["environmentVariables", selectedEnvironment]);
      setNewVariable({ key: "", value: "" });
      setShowAddForm(false);
    },
    onError: (error) => {
      notify("Error Creating Variable", "error");
      console.error("Error adding variable:", error);
    },
  });

  const addGlobalVariableMutation = useAddGlobalVariable({
    onSuccess: () => {
      notify("Global Variable Created Successfully!", "success");
      queryClient.invalidateQueries(["globalVariables"]);
      setNewGlobalVariable({ key: "", value: "" });
      setShowAddGlobalForm(false);
    },
    onError: (error) => {
      notify("Error Creating Global Variable", "error");
      console.error("Error adding global variable:", error);
    },
  });

  const updateVariableSourceMutation = useUpdateVariableSource({
    onSuccess: () => {
      notify("Variable source updated successfully!", "success");
      queryClient.invalidateQueries(["environmentVariables", selectedEnvironment]);
      queryClient.invalidateQueries(["globalVariables"]);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || error?.message || "Failed to update variable source";
      notify(errorMessage, "error");
    },
  });

  const deleteVariableMutation = useDeleteEnvironmentVariableMutation({
    onSuccess: () => {
      notify("Variable Deleted Successfully!", "success");
      queryClient.invalidateQueries(["environmentVariables", selectedEnvironment]);
      setDeleteVariableDialog({ open: false, variable: null });
    },
    onError: (error) => {
      notify(`Error Deleting Variable: ${error.message}`, "error");
      setDeleteVariableDialog({ open: false, variable: null });
    },
  });

  const deleteGlobalVariableMutation = useDeleteGlobalVariable({
    onSuccess: () => {
      notify("Global Variable Deleted Successfully!", "success");
      queryClient.invalidateQueries(["globalVariables"]);
      setDeleteVariableDialog({ open: false, variable: null });
    },
    onError: (error) => {
      notify(`Error Deleting Global Variable: ${error.message}`, "error");
      setDeleteVariableDialog({ open: false, variable: null });
    },
  });

  const { mutate: deleteEnvironment } = useDeleteEnvironment({
    onSuccess: () => {
      notify("Environment Deleted Successfully!", "success");
      queryClient.invalidateQueries(["environments"]);
      setDeleteEnvironmentDialog({ open: false, environment: null });
    },
    onError: (error) => {
      notify(`Error Deleting Environment ${error}`);
      setDeleteEnvironmentDialog({ open: false, environment: null });
    },
  });

  const createEnvironmentMutation = useCreateEnvironmentMutation({
    onSuccess: () => {
      notify("Environment Created Successfully!", "success");
      queryClient.invalidateQueries(["environments"]);
      setNewEnvironment({ name: "" });
      setShowAddEnvironmentForm(false);
    },
    onError: (error) => {
      notify(`Error creating environment ${error}`);
    },
  });

  const handleAddVariable = () => {
    if (newVariable.key && newVariable.value) {
      addVariableMutation.mutate({
        variableData: {
          environmentId: selectedEnvironment,
          variable: newVariable.key,
          value: newVariable.value,
        },
      });
    }
  };

  const handleAddGlobalVariable = () => {
    if (newGlobalVariable.key && newGlobalVariable.value) {
      addGlobalVariableMutation.mutate({
        variableData: {
          variable: newGlobalVariable.key,
          value: newGlobalVariable.value,
        },
      });
    }
  };

  const handleAddEnvironment = () => {
    if (newEnvironment.name) {
      createEnvironmentMutation.mutate(newEnvironment);
    }
  };

  const handleUpdateSource = ({ formFields, file }) => {
    updateVariableSourceMutation.mutate({ formFields, file });
  };

  const handleDeleteVariableClick = (variable) => {
    setDeleteVariableDialog({ open: true, variable });
  };

  const handleDeleteEnvironmentClick = (environment) => {
    setDeleteEnvironmentDialog({ open: true, environment });
  };

  const confirmDeleteVariable = () => {
    if (deleteVariableDialog.variable) {
      if (showGlobalVariables) {
        deleteGlobalVariableMutation.mutate({
          variableId: deleteVariableDialog.variable.id,
        });
      } else {
        deleteVariableMutation.mutate({
          variableId: deleteVariableDialog.variable.id,
        });
      }
    }
  };

  const confirmDeleteEnvironment = () => {
    if (deleteEnvironmentDialog.environment) {
      deleteEnvironment(deleteEnvironmentDialog.environment.id);
    }
  };

  const handleGlobalVariablesClick = () => {
    setShowGlobalVariables(true);
    setSelectedEnvironment(null);
  };

  const handleEnvironmentClick = (envId) => {
    setSelectedEnvironment(envId);
    setShowGlobalVariables(false);
  };

  return (
    <div className="font-apple bg-white min-h-screen">
      {/* Header */}
      <div className=" p-4 border-gray-200 border flex flex-row items-center mb-4">
        <h2 className="text-lg font-semibold"> Configure & Manage Your Environments</h2>
        <Container className="ml-8 text-green-500" />
      </div>

      <div className="max-w-7xl mx-auto py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Environments Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-medium text-gray-900">Environments</h2>
                  <button
                    onClick={() => setShowAddEnvironmentForm(true)}
                    className="text-sm text-blue-600 hover:text-blue-700 transition-colors flex items-center cursor-pointer"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Create New
                  </button>
                </div>
              </div>

              {/* Add Environment Form */}
              {showAddEnvironmentForm && (
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-base font-medium text-gray-900 mb-4">
                    Create New Environment
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">Name</label>
                      <input
                        type="text"
                        value={newEnvironment.name}
                        onChange={(e) =>
                          setNewEnvironment((prev) => ({ ...prev, name: e.target.value }))
                        }
                        placeholder="Environment name"
                        className="w-full px-3 py-2 text-sm border border-gray-200 focus:outline-none focus:border-blue-400 cursor-text rounded"
                      />
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setShowAddEnvironmentForm(false);
                          setNewEnvironment({ name: "" });
                        }}
                        className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer border border-gray-200 rounded hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddEnvironment}
                        disabled={createEnvironmentMutation.isPending || !newEnvironment.name}
                        className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer rounded disabled:opacity-50"
                      >
                        {createEnvironmentMutation.isPending ? "Creating..." : "Create Environment"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="p-3 max-h-[calc(100vh-250px)] overflow-y-scroll">
                {/* Global Variables  */}
                <button
                  onClick={handleGlobalVariablesClick}
                  className={`w-full text-left px-3 py-3 text-base transition-colors border rounded cursor-pointer mb-2 ${
                    showGlobalVariables
                      ? "bg-green-50 border-green-200 text-green-900"
                      : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Globe className="w-4 h-4" />
                    <span>Global Variables</span>
                  </div>
                </button>

                {environmentsLoading ? (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Loading environments...
                  </div>
                ) : (
                  <div className="space-y-1">
                    {environments?.map((env) => (
                      <button
                        key={env.id}
                        onClick={() => handleEnvironmentClick(env.id)}
                        className={`w-full text-left px-3 py-3 text-base transition-colors border rounded cursor-pointer ${
                          selectedEnvironment === env.id
                            ? "bg-blue-50 border-blue-200 text-blue-900"
                            : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{env.environmentName}</span>
                          <button
                            className="hover:text-red-500 cursor-pointer text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteEnvironmentClick(env);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Variables Panel */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-medium text-gray-900">
                    {showGlobalVariables
                      ? "Global Variables"
                      : selectedEnvironment
                        ? `Variables for ${environments.find((e) => e.id === selectedEnvironment)?.environmentName}`
                        : "Select an environment"}
                  </h2>
                  {(selectedEnvironment || showGlobalVariables) && (
                    <button
                      onClick={() =>
                        showGlobalVariables ? setShowAddGlobalForm(true) : setShowAddForm(true)
                      }
                      className="text-sm text-blue-600 hover:text-blue-700 transition-colors flex items-center cursor-pointer"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Variable
                    </button>
                  )}
                </div>
              </div>

              <div className="p-4 max-h-[calc(100vh-200px)] overflow-y-scroll">
                {!selectedEnvironment && !showGlobalVariables ? (
                  <div className="text-center py-12 text-gray-500">
                    <p className="text-base mb-3">
                      Select an environment or global variables to view and manage variables
                    </p>
                    <p className="text-sm text-gray-400">
                      Choose an option from the left panel to get started
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Add Variable Form */}
                    {(showAddForm || showAddGlobalForm) && (
                      <div className="mb-4 p-4 border border-gray-200 bg-gray-50 rounded">
                        <h3 className="text-base font-medium text-gray-900 mb-4">
                          {showGlobalVariables ? "Add New Global Variable" : "Add New Variable"}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm text-gray-600 mb-2">Key</label>
                            <input
                              type="text"
                              value={showGlobalVariables ? newGlobalVariable.key : newVariable.key}
                              onChange={(e) =>
                                showGlobalVariables
                                  ? setNewGlobalVariable((prev) => ({
                                      ...prev,
                                      key: e.target.value,
                                    }))
                                  : setNewVariable((prev) => ({ ...prev, key: e.target.value }))
                              }
                              placeholder="VARIABLE_NAME"
                              className="w-full px-3 py-2 text-sm border border-gray-200 font-mono focus:outline-none focus:border-blue-400 cursor-text rounded"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-600 mb-2">Value</label>
                            <input
                              type="text"
                              value={
                                showGlobalVariables ? newGlobalVariable.value : newVariable.value
                              }
                              onChange={(e) =>
                                showGlobalVariables
                                  ? setNewGlobalVariable((prev) => ({
                                      ...prev,
                                      value: e.target.value,
                                    }))
                                  : setNewVariable((prev) => ({ ...prev, value: e.target.value }))
                              }
                              placeholder="Variable value"
                              className="w-full px-3 py-2 text-sm border border-gray-200 font-mono focus:outline-none focus:border-blue-400 cursor-text rounded"
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex space-x-3">
                            <button
                              onClick={() => {
                                showGlobalVariables
                                  ? setShowAddGlobalForm(false)
                                  : setShowAddForm(false);
                                setNewVariable({ key: "", value: "" });
                                setNewGlobalVariable({ key: "", value: "" });
                              }}
                              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer border border-gray-200 rounded hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={
                                showGlobalVariables ? handleAddGlobalVariable : handleAddVariable
                              }
                              disabled={
                                isDisabled ||
                                (showGlobalVariables
                                  ? addGlobalVariableMutation.isPending
                                  : addVariableMutation.isPending)
                              }
                              className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer rounded disabled:opacity-50"
                            >
                              {showGlobalVariables
                                ? addGlobalVariableMutation.isPending
                                  ? "Adding..."
                                  : "Add Global Variable"
                                : addVariableMutation.isPending
                                  ? "Adding..."
                                  : "Add Variable"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Variables List */}
                    <div className="space-y-3">
                      {(showGlobalVariables ? globalVariablesLoading : variablesLoading) ? (
                        <div className="text-center py-8 text-gray-500">Loading variables...</div>
                      ) : (showGlobalVariables ? globalVariables : currentVariables)?.length ===
                        0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <p className="text-base mb-3">
                            {showGlobalVariables
                              ? "No global variables configured"
                              : "No variables configured for this environment"}
                          </p>
                          <button
                            onClick={() =>
                              showGlobalVariables
                                ? setShowAddGlobalForm(true)
                                : setShowAddForm(true)
                            }
                            className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer rounded"
                          >
                            Add your first variable
                          </button>
                        </div>
                      ) : (
                        (showGlobalVariables ? globalVariables : currentVariables)?.map(
                          (variable) => (
                            <div
                              key={variable.id}
                              className="border border-gray-200 rounded bg-white"
                            >
                              {/* Variable Header */}
                              {/* Variable Header */}
                              <div className="flex items-center justify-between py-3 px-3 bg-gray-50">
                                {editingVariable?.id === variable.id ? (
                                  // Edit mode
                                  <div className="flex-1 flex items-center space-x-3">
                                    <input
                                      type="text"
                                      value={editingVariable.variable}
                                      onChange={(e) =>
                                        setEditingVariable((prev) => ({
                                          ...prev,
                                          variable: e.target.value,
                                        }))
                                      }
                                      placeholder="VARIABLE_NAME"
                                      className="px-3 py-1.5 text-sm border border-gray-300 font-mono focus:outline-none focus:border-blue-400 rounded"
                                    />
                                    <span className="text-gray-400">=</span>
                                    <input
                                      type="text"
                                      value={editingVariable.value}
                                      onChange={(e) =>
                                        setEditingVariable((prev) => ({
                                          ...prev,
                                          value: e.target.value,
                                        }))
                                      }
                                      placeholder="Variable value"
                                      className="flex-1 px-3 py-1.5 text-sm border border-gray-300 font-mono focus:outline-none focus:border-blue-400 rounded"
                                    />
                                    <div className="flex items-center space-x-2">
                                      <button
                                        onClick={() => {
                                          updateVariable({
                                            variableId: editingVariable.id,
                                            variableData: {
                                              variable: editingVariable.variable,
                                              value: editingVariable.value,
                                            },
                                          });
                                          setEditingVariable(null);
                                        }}
                                        disabled={
                                          !editingVariable.variable.trim() ||
                                          !editingVariable.value.trim() ||
                                          updatingVariable
                                        }
                                        className="px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded disabled:opacity-50 cursor-pointer"
                                      >
                                        {updatingVariable ? "Saving..." : "Save"}
                                      </button>
                                      <button
                                        onClick={() => setEditingVariable(null)}
                                        className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded cursor-pointer"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  // View mode
                                  <>
                                    <div className="flex-1 min-w-0 flex items-center space-x-4">
                                      {/* Variable Name */}
                                      <div className="flex items-center space-x-2">
                                        <p className="text-base font-mono text-gray-900 font-medium">
                                          {variable.variable}
                                        </p>
                                        {showGlobalVariables && (
                                          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                                            Global
                                          </span>
                                        )}
                                        {variable.isSecret && (
                                          <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                                            Secret
                                          </span>
                                        )}
                                      </div>

                                      {/* Variable Value */}
                                      <div className="flex items-center space-x-2">
                                        <span className="text-gray-400">=</span>
                                        <p className="text-sm font-mono text-gray-600 break-all">
                                          {variable.value}
                                        </p>
                                      </div>
                                    </div>

                                    <div className="flex items-center space-x-4 ml-4">
                                      <button
                                        onClick={() => setEditingVariable(variable)}
                                        disabled={
                                          showGlobalVariables
                                            ? deleteGlobalVariableMutation.isPending
                                            : deleteVariableMutation.isPending || updatingVariable
                                        }
                                        className="text-gray-400 hover:text-blue-500 transition-colors cursor-pointer disabled:opacity-50"
                                      >
                                        <EditIcon className="w-4 h-4 font-bold" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteVariableClick(variable)}
                                        disabled={
                                          showGlobalVariables
                                            ? deleteGlobalVariableMutation.isPending
                                            : deleteVariableMutation.isPending
                                        }
                                        className="text-gray-400 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>

                              {!showGlobalVariables && (
                                <div className="px-3 pb-3">
                                  <VariableSourceEditor
                                    variable={variable}
                                    environmentId={selectedEnvironment}
                                    onUpdate={handleUpdateSource}
                                    isUpdating={updateVariableSourceMutation.isPending}
                                  />
                                </div>
                              )}
                            </div>
                          ),
                        )
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Variable Dialog */}
      <Dialog
        open={deleteVariableDialog.open}
        onClose={() => setDeleteVariableDialog({ open: false, variable: null })}
        PaperProps={{
          style: {
            fontFamily: "inherit",
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle style={{ fontWeight: 600, fontSize: "1.125rem" }}>
          Delete {showGlobalVariables ? "Global " : ""}Variable
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ color: "#6b7280", fontSize: "0.875rem" }}>
            Are you sure you want to delete the {showGlobalVariables ? "global " : ""}variable{" "}
            <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#374151" }}>
              {deleteVariableDialog.variable?.variable}
            </span>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setDeleteVariableDialog({ open: false, variable: null })}
            style={{
              color: "#6b7280",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteVariable}
            style={{
              backgroundColor: "#dc2626",
              color: "white",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
            variant="contained"
            disabled={
              showGlobalVariables
                ? deleteGlobalVariableMutation.isPending
                : deleteVariableMutation.isPending
            }
          >
            {(
              showGlobalVariables
                ? deleteGlobalVariableMutation.isPending
                : deleteVariableMutation.isPending
            )
              ? "Deleting..."
              : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Environment Dialog */}
      <Dialog
        open={deleteEnvironmentDialog.open}
        onClose={() => setDeleteEnvironmentDialog({ open: false, environment: null })}
        PaperProps={{
          style: {
            fontFamily: "inherit",
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle style={{ fontWeight: 600, fontSize: "1.125rem" }}>
          Delete Environment
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ color: "#6b7280", fontSize: "0.875rem" }}>
            Are you sure you want to delete the environment{" "}
            <span style={{ fontWeight: 600, color: "#374151" }}>
              {deleteEnvironmentDialog.environment?.environmentName}
            </span>
            ? This will also delete all variables in this environment. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setDeleteEnvironmentDialog({ open: false, environment: null })}
            style={{
              color: "#6b7280",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteEnvironment}
            style={{
              backgroundColor: "#dc2626",
              color: "white",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
            variant="contained"
          >
            Delete Environment
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Variables;
